import React from 'react';
import { observer } from 'mobx-react';
import { SvgIcon, Box } from 'components';
import { useViewerControl } from 'libs/rendering/gltf/hooks';
import color from 'styles/themes/wikifactory/styles/colors';
import { ErrorItemStyled } from './errorItem.styled';

interface ErrorItemProps {
  warning: WarningObjectProps; // the warning object
  icon?: React.ReactNode;
}

// wasm [DfmWarning] object
interface WarningObjectProps {
  selfIx: number;
  type: object;
  value: number;
  isAngle: boolean;
  area: number;
  volumeRatio: number;
}

export const ErrorItem = observer(({ icon, warning }: ErrorItemProps) => {
  if (!warning) return null;

  const viewerControl = useViewerControl();
  const settings = viewerControl?.viewer?.settings;
  const selManager = viewerControl?.viewer?.selectionManager;
  if (!settings || !selManager) return null;

  // check if this warning item is currently selected/highlighted
  const selected = selManager.IsModelItemSelectionTarget(warning);
  const highlighted = selManager.IsModelItemHighlightTarget(warning);

  /*
    events
  */
  const handleHover = () => {
    // highlight warning item
    viewerControl?.viewer?.selectionManager?.ClearHighlight();
    settings.DrawDfmWarning(warning.type, warning.selfIx, false);
  };

  const handleClick = () => {
    // select warning item
    settings.DrawDfmWarning(warning.type, warning.selfIx, true);
  };

  /*
    render
  */

  const wIndex = `${warning.selfIx + 1}.`;

  const wValue = warning.isAngle
    ? `${warning.value.toFixed(0)}°`
    : `${warning.value.toFixed(2)}`;

  let wSize = '';
  if (warning.isAngle) wSize = `len ${warning.area.toFixed(1)}`;
  else if (warning.volumeRatio > 0)
    wSize = `vol﹪ ${(100 * warning.volumeRatio).toFixed(2)}`;

  return (
    <ErrorItemStyled
      selected={selected}
      highlighted={highlighted}
      onMouseEnter={handleHover}
      onMouseLeave={() => selManager.ClearHighlight()}
      onClick={handleClick}
    >
      {icon && <SvgIcon icon={icon} size='medium' />}
      <Box mr={4} sx={{ width: '28px', textAlign: 'right' }}>
        {wIndex}
      </Box>
      <Box ml={1} sx={{ width: '40px', color: color.wfBlack }}>
        {wValue}
      </Box>
      <Box
        ml={3}
        sx={{
          width: '70px',
          color: color.wfGrey600,
          fontWeight: 'normal', // so this doesn't get bold on highlight
        }}
      >
        {wSize}
      </Box>
    </ErrorItemStyled>
  );
});
